import type { User } from '@creator/sdk/modules/account/account.model';
import type { DomainConfig } from '@creator/sdk/modules/config/config.model';
import type { DomainsSymbols } from '@creator/sdk/modules/public-settings/public-settings.model';
import type { Token } from '@creator/sdk/modules/token/token.model';
import { isObjectEmpty } from '@creator/ui/utils/object-utils';
import store from '@src/store';
import type { Post } from '@src/store/contentEntities/model';
import { type PostCollectionCompositeKeyParams, postCollectionAtomFamily, setPosts } from '@src/store/posts/posts';
import { jotaiStore } from '@src/store/store';

export interface SSRData {
    i18n: Record<string, string>;
    domainConfig: DomainConfig;
    domainSymbols: DomainsSymbols;
    tokens?: Record<Token['tokenName'], Token>;
    users?: Record<User['id'], User>;
    partnerPosts?: Record<Post['id'], Post>;
    partnerPostsFlairId?: string;
    posts?: Record<Post['id'], Post>;
    pinnedPartnerPosts?: Record<Post['id'], Post>;
    pinnedCommunityPosts?: Record<Post['id'], Post>;
    metaParams: Record<string, string>;
    whitelistedUsers?: Record<Token['tokenName'], User['id'][]>;
}

export function processSSRData() {
    const ssrData = window.__SSR_DATA__;
    if (!ssrData) {
        console.warn('SSR Data (__SSR_DATA__) not found on window.');
        return null; // Or handle as appropriate
    }

    const { tokens = {}, users, posts = {}, partnerPosts = {}, partnerPostsFlairId, pinnedPartnerPosts = {}, pinnedCommunityPosts = {}, whitelistedUsers, domainSymbols } = ssrData;

    // 1. Hydrate the global post cache
    const allPostsFromSSR = { ...posts, ...partnerPosts, ...pinnedPartnerPosts, ...pinnedCommunityPosts };
    setPosts(allPostsFromSSR);

    // 2. Hydrate Partner Posts Collection State
    if (!isObjectEmpty(partnerPosts)) {
        const postIds = Object.keys(partnerPosts);
        const firstPartnerPost = partnerPosts[postIds[0]!];

        const { tokenName } = firstPartnerPost!;
        const params: PostCollectionCompositeKeyParams = {
            type: 'partner',
            tokenName,
            flairId: partnerPostsFlairId,
        };

        // Get the specific atom instance from the family
        const partnerCollectionAtom = postCollectionAtomFamily(params);

        // Set the initial state for this collection
        jotaiStore.set(partnerCollectionAtom, {
            ids: postIds,
            // We don't get the actual DocumentSnapshot from SSR, so set lastDoc to null.
            // The first 'loadMore' call will fetch the next page correctly.
            // lastDoc: lastPost.id,
            lastDoc: null,
            hasMore: true,
            status: 'success', // Data is successfully loaded from SSR
            error: null,
        });
    }

    // 3. Hydrate Pinned Partner Posts Collection State
    if (!isObjectEmpty(pinnedPartnerPosts)) {
        const pinnedPartnerPostIds = Object.keys(pinnedPartnerPosts);
        const firstPinnedPartnerPost = pinnedPartnerPosts[pinnedPartnerPostIds[0]!];
        const { tokenName } = firstPinnedPartnerPost!;

        const pinnedPartnerCollectionAtom = postCollectionAtomFamily({ type: 'pinnedPartner', tokenName, flairId: partnerPostsFlairId });
        jotaiStore.set(pinnedPartnerCollectionAtom, {
            ids: pinnedPartnerPostIds,
            lastDoc: null,
            hasMore: false,
            status: 'success',
            error: null,
        });
    }

    // 4. Hydrate Pinned Community Posts Collection State
    if (!isObjectEmpty(pinnedPartnerPosts)) {
        const pinnedPartnerPostIds = Object.keys(pinnedPartnerPosts);
        const firstPinnedPartnerPost = pinnedPartnerPosts[pinnedPartnerPostIds[0]!];
        const { tokenName } = firstPinnedPartnerPost!;

        const pinnedPartnerCollectionAtom = postCollectionAtomFamily({ type: 'pinnedPartner', tokenName });
        jotaiStore.set(pinnedPartnerCollectionAtom, {
            ids: pinnedPartnerPostIds,
            lastDoc: null,
            hasMore: false,
            status: 'success',
            error: null,
        });
    }

    // 4. Hydrate Pinned Community Posts Collection State
    if (!isObjectEmpty(pinnedCommunityPosts)) {
        const pinnedCommunityPostIds = Object.keys(pinnedCommunityPosts);
        const firstPinnedCommunityPost = pinnedCommunityPosts[pinnedCommunityPostIds[0]!];
        const { tokenName } = firstPinnedCommunityPost!;

        const pinnedCommunityCollectionAtom = postCollectionAtomFamily({ type: 'pinnedCommunity', tokenName });
        jotaiStore.set(pinnedCommunityCollectionAtom, {
            ids: pinnedCommunityPostIds,
            lastDoc: null,
            hasMore: false,
            status: 'success',
            error: null,
        });
    }

    if (tokens && !isObjectEmpty(tokens)) store.getActions().token.setTokens(tokens);
    if (users && !isObjectEmpty(users)) store.getActions().user.setUsersDict(users);
    if (whitelistedUsers && !isObjectEmpty(whitelistedUsers)) store.getActions().token.setTokenPartners(whitelistedUsers);
    if (domainSymbols) store.getActions().publicSettings.set({ key: 'domainsSymbols', value: domainSymbols });

    return ssrData; // Return the original data if needed elsewhere
}
